import type { Chord, ChordQuality, ChordExtensions } from '@/types/chord';
import type { Move, MoveContext, MoveResult } from '../types';

/**
 * Every 7th/9th/11th/13th/altered quality collapses to its triad; plain
 * triads map to themselves (they only get here via the "any extension"
 * applicability leg, so their quality doesn't need to change).
 */
const TRIAD_FOR_QUALITY: Record<ChordQuality, ChordQuality> = {
  major: 'major',
  minor: 'minor',
  diminished: 'diminished',
  augmented: 'augmented',
  dom7: 'major',
  maj7: 'major',
  min7: 'minor',
  halfdim7: 'diminished',
  dim7: 'diminished',
  dom9: 'major',
  maj9: 'major',
  min9: 'minor',
  dom11: 'major',
  min11: 'minor',
  dom13: 'major',
  maj13: 'major',
  min13: 'minor',
  alt: 'major',
  dom7b9: 'major',
  dom7sharp9: 'major',
  dom7sharp11: 'major',
};

function hasAnyExtension(extensions: ChordExtensions): boolean {
  return Object.values(extensions).some(Boolean);
}

/** thin it — reduce to triad. Clears extensions and collapses 7th+ qualities. */
export const thinIt: Move = {
  id: 'thin-it',
  name: 'thin it',
  theory: 'reduce to triad',

  applicable(ctx: MoveContext): boolean {
    const chord = ctx.chords[ctx.index];
    if (!chord) return false;
    return hasAnyExtension(chord.extensions) || TRIAD_FOR_QUALITY[chord.quality] !== chord.quality;
  },

  apply(ctx: MoveContext): MoveResult {
    const chord = ctx.chords[ctx.index];
    const changed: Chord = {
      ...chord,
      quality: TRIAD_FOR_QUALITY[chord.quality],
      extensions: {},
      key: ctx.key,
      mode: ctx.mode,
    };
    const chords: Chord[] = ctx.chords.map((c, i) => (i === ctx.index ? changed : c));

    return {
      chords,
      changedIds: [changed.id],
      explanation: 'stripped it back to the three notes that matter.',
      theoryLabel: 'reduced to triad',
    };
  },
};
